// Чтение карты доступности клиентом: чистые функции, ноль React и ноль сети.
//
// Здесь НЕ переоткрываются правила. Состояния считает сервер
// (`resolveModuleAvailability`), а колонку «Навигация/панель» матрицы §6.1 он
// же и присылает готовой (`entry.nav` — результат `navVisibilityFor`). Этот
// файл отвечает ровно за два вопроса, которых у сервера нет:
//   1) что делать, когда карты НЕТ вовсе (первый запуск офлайн);
//   2) какому модулю принадлежит открытый путь.

import type {
	ModuleAvailabilityEntry,
	ModuleAvailabilityOutput,
} from "@studyhub/shared/contracts/modules";
import type { Availability, ModuleId, ModuleNavPresentation } from "@studyhub/shared/module";
import { WEB_MODULE_REGISTRY } from "@/lib/module-registry";
import type { WebModuleManifest } from "@/modules/_contract";

/**
 * Откуда взята карта.
 *
 * `unknown` — это НЕ «всё выключено» и не «всё включено», а третье состояние:
 * мы ещё ничего не знаем. Оно существует отдельно от `Availability` намеренно —
 * иначе первый офлайн-запуск пришлось бы кодировать существующим состоянием,
 * и любой выбор был бы ложью: `available` разрешил бы тратить квоту в возможно
 * выключенном модуле, `read_only`/`hidden` — вычистил бы навигацию и показал
 * пустой продукт человеку в метро.
 */
export type AvailabilitySource = "server" | "snapshot" | "unknown";

export interface ModuleAvailabilityView {
	readonly source: AvailabilitySource;
	/**
	 * Индекс по id. Сервер отдаёт карту массивом (стабильный порядок, дешевле
	 * в JSON), но потребители спрашивают «что с этим модулем» — а это поиск по
	 * ключу на каждый пункт меню и на каждую плитку. Индекс строится один раз
	 * при записи в стор, а не заново в каждом `find`.
	 */
	readonly byId: ReadonlyMap<ModuleId, ModuleAvailabilityEntry>;
}

export const UNKNOWN_AVAILABILITY_VIEW: ModuleAvailabilityView = {
	source: "unknown",
	byId: new Map(),
};

/** Ответ сервера (или снапшот) → индекс для чтения. */
export function indexAvailability(
	modules: ModuleAvailabilityOutput["modules"],
): Map<ModuleId, ModuleAvailabilityEntry> {
	return new Map(modules.map((entry) => [entry.id, entry]));
}

/**
 * Состояние модуля или `null`, если ответа нет.
 *
 * `null` возвращается в двух разных случаях, и оба означают «гейт неприменим»:
 *   • карты нет вовсе (`source: "unknown"`);
 *   • карта есть, но записи о модуле в ней нет.
 *
 * Второй случай не гипотетический: манифесты подключаются этапами (Ш2 — ядро,
 * Ш5 — остальные девять), и модуль без манифеста платформе неизвестен. Считать
 * его скрытым — значит на время между этапами выключить половину продукта;
 * поэтому «нет манифеста» = «ведёт себя как до модульной платформы».
 */
export function availabilityEntryOf(
	view: ModuleAvailabilityView,
	moduleId: ModuleId,
): ModuleAvailabilityEntry | null {
	if (view.source === "unknown") return null;
	return view.byId.get(moduleId) ?? null;
}

/** Состояние в форме резолвера — для потребителей, ждущих `Availability`. */
export function availabilityOf(
	view: ModuleAvailabilityView,
	moduleId: ModuleId,
): Availability | null {
	const entry = availabilityEntryOf(view, moduleId);
	if (!entry) return null;
	if (entry.state === "available") return { state: "available" };
	// `reason` отсутствует только у `available`; у остальных состояний контракт
	// его требует по смыслу, но приехать по сети может что угодно — подстилаемся
	// самой мягкой причиной, чтобы не притвориться аварией.
	const reason = entry.reason ?? "product_disabled";
	if (entry.state === "teaser") return { state: "teaser", reason: "tier_locked" };
	return { state: entry.state, reason };
}

/**
 * Показ пункта в навигации.
 *
 * Решение приходит с сервера полем `nav` — ровно затем, чтобы шесть
 * поверхностей не переоткрывали матрицу §6.1 каждая у себя. Клиент добавляет
 * единственное правило, которого у сервера нет: карты нет → пункт ВИДЕН.
 * Пустое меню офлайн читается как поломка приложения, а не как «модули
 * выключены».
 */
export function navPresentationOf(
	view: ModuleAvailabilityView,
	moduleId: ModuleId,
): ModuleNavPresentation {
	return availabilityEntryOf(view, moduleId)?.nav ?? "visible";
}

/**
 * Можно ли открыть страницу модуля — fail-open на чтение.
 *
 * `read_only` пропускается всегда: пользователь обязан видеть свои артефакты
 * выключенного модуля. Закрыт только `hidden`.
 */
export function canOpenModule(view: ModuleAvailabilityView, moduleId: ModuleId): boolean {
	return availabilityEntryOf(view, moduleId)?.state !== "hidden";
}

/**
 * Можно ли запускать создание/генерацию — fail-closed.
 *
 * Отличие от `canOpenModule` принципиальное: при неизвестной карте чтение
 * разрешаем, а трату квоты в возможно выключенном модуле — нет. Модуль без
 * записи (карта известна, манифеста ещё нет) генерировать по-прежнему может:
 * он просто вне платформы, гейта для него не существует.
 */
export function canGenerateInModule(view: ModuleAvailabilityView, moduleId: ModuleId): boolean {
	if (view.source === "unknown") return false;
	const entry = view.byId.get(moduleId);
	if (!entry) return true;
	return entry.state === "available";
}

// ─── Кому принадлежит путь ──────────────────────────────────────────────────

interface RouteOwner {
	readonly prefix: string;
	readonly moduleId: ModuleId;
}

/**
 * Плоский список «префикс → модуль», отсортированный от длинного к короткому.
 *
 * Порядок — не оптимизация, а корректность: `/courses/calendar` принадлежит
 * календарю, а не курсам, и выиграть обязан более длинный префикс. Ровно та же
 * проблема, что у подсветки пункта навигации (`isNavItemActive`).
 */
function buildRouteOwners(registry: readonly WebModuleManifest[]): RouteOwner[] {
	const owners: RouteOwner[] = [];
	for (const manifest of registry) {
		for (const prefix of manifest.routes) owners.push({ prefix, moduleId: manifest.id });
	}
	return owners.toSorted((a, b) => b.prefix.length - a.prefix.length);
}

const ROUTE_OWNERS = buildRouteOwners(WEB_MODULE_REGISTRY);

/** Модуль-владелец пути (`null` — путь платформе не принадлежит). */
export function matchModuleByPath(
	pathname: string,
	owners: readonly RouteOwner[] = ROUTE_OWNERS,
): ModuleId | null {
	for (const owner of owners) {
		if (pathname === owner.prefix || pathname.startsWith(`${owner.prefix}/`)) {
			return owner.moduleId;
		}
	}
	return null;
}

/** Тот же матчинг для произвольного реестра — вход контрольного теста. */
export function routeOwnersOf(registry: readonly WebModuleManifest[]): RouteOwner[] {
	return buildRouteOwners(registry);
}

/**
 * Куда уводить с закрытой страницы.
 *
 * Не на `/`: устаревшая ссылка из письма или рекомендации, схлопнутая в главную,
 * выглядит как потеря раздела. Ведём в ближайшую живую поверхность, где лежат
 * данные того же порядка — Библиотека, затем Обучение, и только если закрыты и
 * они, на главную (она модулем не является и выключена быть не может).
 */
const FALLBACK_CANDIDATES: readonly { to: string; moduleId: ModuleId }[] = [
	{ to: "/library", moduleId: "library" },
	{ to: "/courses", moduleId: "courses" },
];

export function fallbackRouteFor(view: ModuleAvailabilityView, from: ModuleId): string {
	for (const candidate of FALLBACK_CANDIDATES) {
		if (candidate.moduleId === from) continue;
		if (navPresentationOf(view, candidate.moduleId) !== "hidden") return candidate.to;
	}
	return "/";
}

/** i18n-ключ названия модуля (namespace `layout`) — для текста пользователю. */
export function moduleLabelKey(moduleId: ModuleId): string | null {
	return WEB_MODULE_REGISTRY.find((m) => m.id === moduleId)?.labelKey ?? null;
}
